import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
  FormHelperText,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Flex, // Import Flex for responsive layouts
  Text,
  Tooltip,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "./AppDataTable";
import {
  tableCustomStyles,
  ReservationStatusDot,
} from "../themes/TableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faSyringe as faSyringeIcon,
  faMoneyBillWave,
  faDollarSign,
  faTrash,
  faCalculator,
  faGears,
  faCapsules,
  faPlus,
  faPenToSquare,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import { getDateFromISOStringIgnoringTimezone } from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";
import SearchableSelect from "./SearchableSelect";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";
import {
  TotalsSummary,
  VaccineDetailCards,
} from "./ReservationVaccineList";
import {
  buildComparableState,
  buildReservationDoc,
  fetchReservationForPrint,
  formatNumber,
  formatThaiDate,
  formatThaiDateTime,
  printReservationDoc,
} from "../utils/reservationDocument";

const VaccineReservationForm = () => {
  const navigate = useNavigate();
  // ลิงก์รูปแบบเดิมที่มีเลขที่ใบจองอยู่ใน URL ยังรองรับไว้ แต่จะถูกเปลี่ยนเป็น URL แบบไม่มีเลขที่ให้
  const { reservationId: legacyReservationId } = useParams();
  const location = useLocation();
  // เลขที่ใบจองส่งผ่าน state ของ router เพื่อไม่ให้ปรากฏใน URL
  const reservationId = location.state?.reservationId || legacyReservationId;
  const isNewReservation = location.pathname.endsWith("/new");
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    ReservationID: reservationId || "RV-XXXX-XX-XX",
    ReservationDate: new Date(),
    FarmerID: profile?.FarmerID || "",
    FarmerName:
      [profile?.FirstName, profile?.LastName].filter(Boolean).join(' ') || "",
    RefTaxID: profile?.FarmerID || "",
    FarmerTel: profile?.Phone || "",
    FarmerEmail: profile?.Email || "",
    UsageObjective: "",
    DeliveryPeriod: "",
    PickupPoint: "",
    ReservedBy: profile?.Email || "",
    ReservationStatus: "ใบจองใหม่",
    ConfirmationEmailSent: false,
    ConsentGiven: true,
    Remark: "",
    Status: "Active",
    VaccineDetails: [],
    documentDate: new Date(),
  });

  const [periodEnd, setPeriodEnd] = useState(null); // Store PeriodEnd date
  const [pickupPoints, setPickupPoints] = useState([]);
  const [newVaccine, setNewVaccine] = useState({ id: "", unitreq: "" });
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(!!reservationId);
  const [errors, setErrors] = useState({});
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [isLeaveAlertOpen, setIsLeaveAlertOpen] = useState(false);
  // สำเนาข้อมูลตามที่บันทึกไว้ล่าสุด ใช้ตรวจว่ามีการแก้ไขที่ยังไม่บันทึกหรือไม่
  const [savedState, setSavedState] = useState(null);
  const cancelRef = useRef();
  const leaveCancelRef = useRef();

  // ตารางรายการวัคซีนมีหลายคอลัมน์ ต่ำกว่า lg จะแคบเกินไป จึงสลับไปใช้การ์ด
  const useCardView = useBreakpointValue(
    { base: true, lg: false },
    { ssr: false }
  );

  const pickupPointOptions = useMemo(
    () =>
      pickupPoints.map((point) => ({
        value: point.WarehouseID,
        label: point.WarehouseName,
      })),
    [pickupPoints]
  );

  // ใส่รหัสวัคซีนไว้ใน label ด้วย เพื่อให้ค้นหาได้ทั้งรหัสและชื่อ
  const vaccineSelectOptions = useMemo(
    () =>
      vaccineOptions.map((vaccine) => ({
        value: vaccine.VaccineID,
        label: `${vaccine.VaccineID} - ${vaccine.VaccineNameTH}`,
      })),
    [vaccineOptions]
  );

  // รายการเดิมที่ซ้ำกับวัคซีนที่กำลังจะเพิ่ม ใช้แสดงจำนวนเดิมใน dialog
  const duplicateVaccine = formData.VaccineDetails.find(
    (detail) => detail.VaccineID === newVaccine.id
  );

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  // ย้ายเลขที่ใบจองจาก URL แบบเดิมไปไว้ใน state เพื่อไม่ให้เลขที่ใบจองค้างอยู่ใน URL
  useEffect(() => {
    if (legacyReservationId) {
      navigate("/vaccinereservationdetail", {
        state: { reservationId: legacyReservationId },
        replace: true,
      });
    }
  }, [legacyReservationId, navigate]);

  // เปิดหน้ารายละเอียดตรง ๆ (เช่น จากบุ๊กมาร์กหรือแท็บใหม่) จะไม่มีเลขที่ใบจองติดมาด้วย
  useEffect(() => {
    if (!reservationId && !isNewReservation) {
      navigate("/vaccinereservationlist", { replace: true });
    }
  }, [reservationId, isNewReservation, navigate]);

  const fetchCycles = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineOrderCycle",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        if (!reservationId) {
          setFormData((prev) => ({
            ...prev,
            DeliveryPeriod: response.CycleName || "",
          }));
          // Fetch PeriodEnd by querying VaccineReservationPeriod
          const periodResponse = await apiService({
            method: "POST",
            url: "/getVaccineReservationPeriod", // Assume new endpoint to fetch period details
            data: { DeliveryPeriod: response.CycleName },
            sessionId: sessionId || "",
          });
          if (periodResponse.result === "success" && periodResponse.PeriodEnd) {
            setPeriodEnd(new Date(periodResponse.PeriodEnd));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching cycles:", error);
    }
  };

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchWarehouseById = async (warehouseId) => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousesByID",
        data: { WarehouseID: warehouseId },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        return response.WarehouseName || warehouseId; // Return WarehouseName or fallback to ID
      }
      return warehouseId; // Fallback to ID if API fails
    } catch (error) {
      console.error(`Error fetching warehouse ${warehouseId}:`, error);
      return warehouseId; // Fallback to ID on error
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesReservationList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
        return response.VaccinesList || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchReservation = async (id, vaccines) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineReservationsByID",
        data: { ReservationID: id },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const reservation = response.VaccineReservation;
        const details = response.VaccineReservationDetails || [];
        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find((v) => v.VaccineID === detail.VaccineID);
          if (!vaccine) {
            console.warn(`VaccineID ${detail.VaccineID} not found in vaccineOptions`);
          }
          const packageSize = vaccine?.PackageSize || 1;
          const doseCount = Math.ceil(detail.RequestQuantity * packageSize);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            LotID: detail.LotID,
            RequestQuantity: detail.RequestQuantity,
            RequestDoses: detail.RequestDoses,
            ApprovedQuantity: detail.ApprovedQuantity,
            ConfirmedQuantity: detail.ConfirmedQuantity,
            UnitPrice: detail.UnitPrice,
            DiscountPercent: detail.DiscountPercent,
            DiscountTotal: detail.DiscountTotal,
            TotalPrice: detail.TotalPrice,
            Remark: detail.Remark,
            DoseCount: doseCount,
          };
        });
        const loadedData = {
          ...formData,
          ...reservation,
          ReservationDate: reservation.ReservationDate ? getDateFromISOStringIgnoringTimezone(reservation.ReservationDate) : new Date(),
          VaccineDetails: updatedDetails,
        };
        setFormData(loadedData);
        setSavedState(buildComparableState(loadedData));
        // Fetch PeriodEnd for the reservation's DeliveryPeriod
        const periodResponse = await apiService({
          method: "POST",
          url: "/getVaccineReservationPeriod",
          data: { DeliveryPeriod: reservation.DeliveryPeriod },
          sessionId: sessionId || "",
        });
        if (periodResponse.result === "success" && periodResponse.PeriodEnd) {
          setPeriodEnd(new Date(periodResponse.PeriodEnd));
        }
      } else {
        throw new Error(response.error || "ไม่สามารถเรียกดูข้อมูลใบจองนี้ได้");
      }
    } catch (error) {
      console.error("Error fetching reservation:", error);
      // ใบจองที่ไม่ใช่ของผู้ใช้รายนี้ หรือไม่มีอยู่ในระบบ ให้กลับไปหน้ารายการ
      alert(error.message || "ไม่สามารถเรียกดูข้อมูลใบจองนี้ได้");
      navigate("/vaccinereservationlist");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      // ระหว่างเปลี่ยนไป URL แบบไม่มีเลขที่ใบจอง ยังไม่ต้องโหลดข้อมูล
      if (sessionId && !legacyReservationId) {
        await fetchCycles();
        await fetchPickupPoints();
        const vaccines = await fetchVaccineOptions();
        if (reservationId) {
          await fetchReservation(reservationId, vaccines);
        }
      }
    };
    loadData();
  }, [sessionId, reservationId, legacyReservationId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleFieldChange(name, value);
  };

  // ใช้กับ input ที่ไม่ได้ส่ง event object มา เช่น SearchableSelect
  const handleFieldChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleVaccineInputChange = (e) => {
    const { name, value } = e.target;
    setNewVaccine({ ...newVaccine, [name]: value });
  };

  const handleQuantityChange = (vaccineId, value) => {
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId) {
        const newQuantity = parseInt(value) || 0;
        const packageSize =
          vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize || 1;
        const newTotalPrice = newQuantity * detail.UnitPrice;
        const newDoseCount = Math.ceil(newQuantity * packageSize);
        return {
          ...detail,
          RequestQuantity: newQuantity,
          TotalPrice: newTotalPrice,
          DoseCount: newDoseCount,
        };
      }
      return detail;
    });
    setFormData({ ...formData, VaccineDetails: updatedDetails });
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "DeliveryPeriod":
      case "PickupPoint":
      case "FarmerName":
      case "FarmerTel":
      case "FarmerEmail":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return (
      formData.DeliveryPeriod &&
      formData.PickupPoint &&
      formData.FarmerName &&
      formData.FarmerTel &&
      formData.FarmerEmail &&
      Object.keys(errors).length === 0
    );
  };

  const handleAddVaccine = () => {
    if (newVaccine.id && newVaccine.unitreq > 0) {
      const selectedVaccine = vaccineOptions.find(
        (v) => v.VaccineID === newVaccine.id
      );
      if (selectedVaccine) {
        const existingVaccine = formData.VaccineDetails.find(
          (v) => v.VaccineID === newVaccine.id
        );
        const uop = selectedVaccine.PriceDistribution || "350";
        const packageSize = selectedVaccine.PackageSize || 1;
        const newQuantity = parseInt(newVaccine.unitreq);
        const newDoseCount = Math.ceil(newQuantity * packageSize);

        if (existingVaccine) {
          setIsAlertOpen(true);
        } else {
          const totalCost = (newQuantity * parseInt(uop)).toString();
          const vaccineData = {
            VaccineID: newVaccine.id,
            name: selectedVaccine.VaccineNameTH,
            LotID: `LT${Date.now().toString().slice(-4)}`,
            RequestQuantity: newQuantity,
            RequestDoses: 0,
            ApprovedQuantity: 0,
            ConfirmedQuantity: 0,
            UnitPrice: parseFloat(uop),
            DiscountPercent: 0,
            DiscountTotal: 0,
            TotalPrice: parseFloat(totalCost),
            Remark: "",
            DoseCount: newDoseCount,
          };
          setFormData({
            ...formData,
            VaccineDetails: [...formData.VaccineDetails, vaccineData],
          });
          setNewVaccine({ id: "", unitreq: "" });
        }
      } else {
        console.warn(`VaccineID ${newVaccine.id} not found in vaccineOptions`);
      }
    }
  };

  const handleAlertConfirm = (action) => {
    const selectedVaccine = vaccineOptions.find(
      (v) => v.VaccineID === newVaccine.id
    );
    const uop = selectedVaccine.PriceDistribution || "350";
    const packageSize = selectedVaccine.PackageSize || 1;
    const newQuantity = parseInt(newVaccine.unitreq);
    const newDoseCount = Math.ceil(newQuantity * packageSize);

    if (action === "add") {
      const updatedDetails = formData.VaccineDetails.map((detail) => {
        if (detail.VaccineID === newVaccine.id) {
          const updatedQuantity = detail.RequestQuantity + newQuantity;
          const newTotalPrice = updatedQuantity * parseFloat(uop);
          const updatedDoseCount = Math.ceil(updatedQuantity * packageSize);
          return {
            ...detail,
            RequestQuantity: updatedQuantity,
            TotalPrice: newTotalPrice,
            DoseCount: updatedDoseCount,
          };
        }
        return detail;
      });
      setFormData({ ...formData, VaccineDetails: updatedDetails });
    } else if (action === "replace") {
      const updatedDetails = formData.VaccineDetails.map((detail) => {
        if (detail.VaccineID === newVaccine.id) {
          const newTotalPrice = newQuantity * parseFloat(uop);
          return {
            ...detail,
            RequestQuantity: newQuantity,
            TotalPrice: newTotalPrice,
            DoseCount: newDoseCount,
          };
        }
        return detail;
      });
      setFormData({ ...formData, VaccineDetails: updatedDetails });
    }
    setNewVaccine({ id: "", unitreq: "" });
    setIsAlertOpen(false);
  };

  const handleRemoveVaccine = (row) => {
    setFormData({
      ...formData,
      VaccineDetails: formData.VaccineDetails.filter(
        (v) => v.VaccineID !== row.VaccineID
      ),
    });
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/createVaccineReservations",
        data: { ...formData, VaccineDetails: [] },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const createdData = {
          ...formData,
          ReservationID: response.ReservationID,
        };
        setFormData(createdData);
        setSavedState(buildComparableState(createdData));
        setIsSubmitted(true);
        alert("สร้างใบจองสำเร็จ! เลขที่ใบจอง: " + response.ReservationID);
      }
    } catch (error) {
      console.error("Error creating reservation:", error);
      alert("เกิดข้อผิดพลาดในการสร้างใบจอง: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    
    const submitData = {
      ...formData,
      ReservationDate: formData.ReservationDate,
      documentDate: formData.documentDate,
    };

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineReservationsByID",
        data: submitData,
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setSavedState(buildComparableState(formData));
        alert("บันทึกใบจองสำเร็จ!");
        //navigate("/vaccinereservationlist");
      }
    } catch (error) {
      console.error("API Error:", error.message);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelReservation = async () => {
    if (window.confirm("คุณแน่ใจหรือไม่ว่าต้องการยกเลิกการจองนี้?")) {
      setIsLoading(true);
      try {
        const response = await apiService({
          method: "POST",
          url: "/cancelVaccineReservationsByID",
          data: { ReservationID: formData.ReservationID },
          sessionId: sessionId || "",
        });
        if (response.result === "success") {
          alert("ยกเลิกการจองสำเร็จ!");
          navigate("/vaccinereservationlist");
        } else {
          alert("เกิดข้อผิดพลาดในการยกเลิก: " + response.error);
        }
      } catch (error) {
        console.error("Error cancelling reservation:", error);
        alert("เกิดข้อผิดพลาดในการยกเลิก: " + error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getWarehouseName = async (warehouseId) => {
    const warehouse = pickupPoints.find((point) => point.WarehouseID === warehouseId);
    if (warehouse) {
      return warehouse.WarehouseName;
    }
    // Fallback to API call if not found in pickupPoints
    return await fetchWarehouseById(warehouseId);
  };

  /**
   * ดึงข้อมูลใบจองจากฐานข้อมูลใหม่ทุกครั้งที่พิมพ์
   * เพื่อไม่ให้เอกสารที่พิมพ์ออกไปมีค่าที่ยังไม่ได้บันทึกจากในฟอร์ม
   */
  /** เตรียมเอกสาร PDF จากข้อมูลล่าสุดในฐานข้อมูล */
  const createReservationDoc = async () => {
    const printData = await fetchReservationForPrint({
      reservationID: formData.ReservationID,
      sessionId,
      vaccineOptions,
    });
    const warehouseName = await getWarehouseName(
      printData.reservation.PickupPoint
    );
    const doc = await buildReservationDoc({
      ...printData,
      warehouseName,
      periodEndDate: periodEnd ? formatThaiDate(periodEnd) : " ",
    });
    return { doc, printData };
  };

  const printTable = async () => {
    setIsLoading(true);
    try {
      const { doc } = await createReservationDoc();
      if (!printReservationDoc(doc)) {
        alert(
          "ไม่สามารถเปิดหน้าต่างพิมพ์เอกสารได้ กรุณาอนุญาตการเปิดหน้าต่างใหม่ (pop-up) ของเว็บไซต์นี้"
        );
      }
    } catch (error) {
      alert("ไม่สามารถพิมพ์ใบจองได้: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToPDF = async () => {
    setIsLoading(true);
    try {
      const { doc, printData } = await createReservationDoc();
      doc.save(`${printData.reservation.ReservationID}.pdf`);
    } catch (error) {
      alert("ไม่สามารถส่งออกเอกสารได้: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  //const isEditable = formData.ReservationStatus === "ใบจองใหม่";
  //const isSaveButtonVisible = periodEnd ? new Date() <= periodEnd : true;
  const today = new Date();
  today.setHours(0, 0, 0, 0); // ตัดเวลาออก เหลือแค่วันที่

  const end = new Date(periodEnd);
  end.setHours(23, 59, 59, 999); // ให้สิ้นสุดจนถึงสิ้นวัน

  const isEditable =
  formData.ReservationStatus === "ใบจองใหม่" &&
  (periodEnd ? today <= end : true);


  const totalQuantity = formData.VaccineDetails.reduce((sum, detail) => sum + detail.RequestQuantity, 0);
  const totalPrice = formData.VaccineDetails.reduce((sum, detail) => sum + detail.TotalPrice, 0);

  // มีการแก้ไขที่ยังไม่บันทึก จึงยังพิมพ์เอกสารไม่ได้
  const hasUnsavedChanges =
    savedState !== null && savedState !== buildComparableState(formData);

  const goBackToList = () => navigate("/vaccinereservationlist");

  // ถ้ายังมีการแก้ไขที่ไม่ได้บันทึก ให้ยืนยันก่อนออกจากหน้า เพื่อไม่ให้ข้อมูลที่แก้ไว้หายไปโดยไม่รู้ตัว
  const handleBack = () => {
    if (hasUnsavedChanges) {
      setIsLeaveAlertOpen(true);
      return;
    }
    goBackToList();
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringeIcon} />
          <span>รหัส / ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      cell: (row) => (
        <Box py={1}>
          <Text fontWeight="medium">{row.VaccineID}</Text>
          <Text fontSize="sm" color="gray.600">
            {row.name}
          </Text>
        </Box>
      ),
      minWidth: "240px",
      wrap: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.RequestQuantity,
      sortable: true,
      right: !isEditable,
      cell: (row) =>
        isEditable ? (
          <Input
            type="number"
            value={row.RequestQuantity}
            onChange={(e) => handleQuantityChange(row.VaccineID, e.target.value)}
            min={0}
            size="sm"
            maxW="110px"
          />
        ) : (
          formatNumber(row.RequestQuantity)
        ),
      minWidth: "130px",
      grow: 0,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCapsules} />
          <span>จำนวน (โดส)</span>
        </HStack>
      ),
      selector: (row) => row.DoseCount,
      format: (row) => formatNumber(row.DoseCount),
      sortable: true,
      right: true,
      minWidth: "120px",
      grow: 0,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faMoneyBillWave} />
          <span>ราคา/ขวด (บาท)</span>
        </HStack>
      ),
      selector: (row) => row.UnitPrice,
      format: (row) => formatNumber(row.UnitPrice, { minimumFractionDigits: 2 }),
      sortable: true,
      right: true,
      minWidth: "150px",
      grow: 0,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faDollarSign} />
          <span>ราคารวม (บาท)</span>
        </HStack>
      ),
      selector: (row) => row.TotalPrice,
      format: (row) =>
        formatNumber(row.TotalPrice, { minimumFractionDigits: 2 }),
      sortable: true,
      right: true,
      minWidth: "150px",
      grow: 0,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faGears} />
          <span>จัดการ</span>
        </HStack>
      ),
      cell: (row) => (
        <IconButton.Delete
          icon={faTrash}
          onClick={() => handleRemoveVaccine(row)}
          label="ลบ"
          size="sm"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      minWidth: "130px",
      omit: !isEditable,
    },
  ];

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 3, sm: 4, md: 6 }}
      m={{ base: 2, md: 4 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <div id="printable-reservation">
        <StandardPageTitle title="รายการสั่งจองวัคซีน" />

        <Box
          borderWidth={1}
          borderRadius="lg"
          boxShadow="lg"
          p={{ base: 3, md: 5 }}
          mb={6}
        >
          <StandardHeader
            title="ข้อมูลเอกสารการสั่งจองวัคซีน"
            bgColor="iBlue.600"
            icon={faFileLines}
          />
          {/* MODIFICATION: Changed Grid to be responsive. It's 1 column on base and 4 on large screens. */}
          <Grid
            templateColumns={{
              base: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            }}
            gap={{ base: 4, md: 6 }}
            p={{ base: 2, md: 5 }}
          >
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  เลขที่ใบสั่งจองวัคซีน
                </FormLabel>
                <Input
                  name="ReservationID"
                  value={formData.ReservationID}
                  isReadOnly
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl isRequired isInvalid={!!errors.DeliveryPeriod}>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  รอบการจัดส่งวัคซีน
                </FormLabel>
                <Input
                  type="text"
                  name="DeliveryPeriod"
                  value={formData.DeliveryPeriod}
                  onChange={handleInputChange}
                placeholder="รอบการจัดส่ง"
                isReadOnly // ✅ ถ้าไม่ต้องการให้ผู้ใช้พิมพ์เอง
              />
                <FormErrorMessage>{errors.DeliveryPeriod}</FormErrorMessage>
              </FormControl>
            </GridItem>
          <GridItem colSpan={{ base: 1, lg: 1 }}>
            <FormControl>
              <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                วันที่สั่งจอง
              </FormLabel>
              <Input
                type="text"
                value={formatThaiDate(formData.ReservationDate)}
                isReadOnly
              />
            </FormControl>
          </GridItem>
{/*                      
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  วันที่เอกสาร
                </FormLabel>
                <CustomDatePicker
                name="ReservationDate"
                value={formData.ReservationDate}
                  onChange={handleInputChange}
                  maxDate={new Date()} // วันนี้
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
*/}
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl isRequired isInvalid={!!errors.PickupPoint}>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  จุดจำหน่าย
                </FormLabel>
                <SearchableSelect
                  name="PickupPoint"
                  value={formData.PickupPoint}
                  onChange={(value) =>
                    handleFieldChange("PickupPoint", value)
                  }
                  options={pickupPointOptions}
                  placeholder="ค้นหา / เลือกจุดจำหน่าย"
                  isInvalid={!!errors.PickupPoint}
                />
                <FormHelperText fontSize="xs">
                  จุดที่ต้องการรับวัคซีน
                </FormHelperText>
                <FormErrorMessage>{errors.PickupPoint}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl isRequired isInvalid={!!errors.FarmerName}>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  ชื่อเกษตรกร
                </FormLabel>
                <Input
                  name="FarmerName"
                  value={formData.FarmerName}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.FarmerName}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  เลขประจำตัวผู้เสียภาษี
                </FormLabel>
                <Input
                  name="RefTaxID"
                  value={formData.RefTaxID}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                  maxLength={30}
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl isRequired isInvalid={!!errors.FarmerTel}>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  เบอร์โทรติดต่อ
                </FormLabel>
                <Input
                  name="FarmerTel"
                  value={formData.FarmerTel}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.FarmerTel}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, lg: 1 }}>
              <FormControl isRequired isInvalid={!!errors.FarmerEmail}>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  E-mail Address
                </FormLabel>
                <Input
                  name="FarmerEmail"
                  value={formData.FarmerEmail}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
                <FormErrorMessage>{errors.FarmerEmail}</FormErrorMessage>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, sm: 2, lg: 4 }}>
              <FormControl mb={4}>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  วัตถุประสงค์การใช้งาน / รายละเอียดเพิ่มเติม
                </FormLabel>
                <Textarea
                  name="UsageObjective"
                  value={formData.UsageObjective}
                  onChange={handleInputChange}
                  colorScheme="iBlue"
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
              <FormControl>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  สถานะรายการ
                </FormLabel>
                <Box pt={1}>
                  <ReservationStatusDot
                    status={formData.ReservationStatus}
                    fontSize={{ base: "md", md: "lg" }}
                  />
                </Box>
              </FormControl>
            </GridItem>
            <GridItem colSpan={{ base: 1, sm: 1, lg: 2 }}>
              <FormControl>
                <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                  วันที่แก้ไขข้อมูล
                </FormLabel>
                <Text pt={1} fontSize={{ base: "md", md: "lg" }}>
                  {formatThaiDateTime(formData.UpdatedAt)}
                </Text>
              </FormControl>
            </GridItem>
          </Grid>
          {!isSubmitted && (
            <Box p={5}>
              {/* MODIFICATION: Replaced HStack with a responsive Flex container */}
              <Flex
                direction={{ base: "column", md: "row" }}
                justifyContent={{ md: "flex-end" }}
                alignItems={{ base: "stretch", md: "center" }}
                gap={4}
                w="100%"
              >
                <IconButton.Cancel
                  onClick={() => navigate("/vaccinereservationlist")}
                />
                <IconButton.Submit
                  label="สร้างใบจองวัคซีน"
                  onClick={handleSubmit}
                />
              </Flex>
            </Box>
          )}
        </Box>

        {isSubmitted && (
          <>
            {isEditable && (
              <Box
                borderWidth={1}
                borderRadius="lg"
                boxShadow="lg"
                p={{ base: 3, md: 5 }}
                mb={6}
              >
                <StandardHeader
                  title="เลือกวัคซีนที่ต้องการสั่งจอง"
                  bgColor="iPurple.600"
                  icon={faSyringe}
                />
                {/* MODIFICATION: Changed Grid to be responsive. It's 1 column on base and 3 on medium screens. */}
                <Grid
                  templateColumns={{ base: "1fr", md: "2fr 1fr auto" }}
                  gap={{ base: 4, md: 6 }}
                  p={{ base: 2, md: 5 }}
                  alignItems="end"
                >
                  <FormControl>
                    <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                      เลือกวัคซีน
                    </FormLabel>
                    <SearchableSelect
                      name="id"
                      value={newVaccine.id}
                      onChange={(value) =>
                        setNewVaccine((prev) => ({ ...prev, id: value }))
                      }
                      options={vaccineSelectOptions}
                      placeholder="พิมพ์รหัสหรือชื่อวัคซีนเพื่อค้นหา..."
                      noOptionsMessage="ไม่พบวัคซีนที่ค้นหา"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize={{ base: "md", md: "lg", xl: "xl" }} color="gray">
                      จำนวน (ขวด)
                    </FormLabel>
                    <Input
                      name="unitreq"
                      type="number"
                      value={newVaccine.unitreq}
                      onChange={handleVaccineInputChange}
                      min={1}
                      colorScheme="iPurple"
                    />
                  </FormControl>
                  <IconButton.Add
                    onClick={handleAddVaccine}
                    w={{ base: "full", md: "auto" }}
                  />
                </Grid>
              </Box>
            )}

            <Box
              borderWidth={1}
              borderRadius="lg"
              boxShadow="lg"
              p={{ base: 3, md: 5 }}
              mb={6}
            >
                <StandardHeader
                  title="รายการวัคซีนที่ต้องการสั่งจอง"
                  bgColor="iOrange.600"
                  icon={faTable}
                />
                <Box p={{ base: 0, md: 5 }}>
                  {useCardView ? (
                    <VStack spacing={4} align="stretch">
                      <VaccineDetailCards
                        details={formData.VaccineDetails}
                        isEditable={isEditable}
                        onQuantityChange={handleQuantityChange}
                        onRemove={handleRemoveVaccine}
                      />
                      <TotalsSummary
                        totalQuantity={totalQuantity}
                        totalPrice={totalPrice}
                      />
                    </VStack>
                  ) : (
                    <Box overflowX="auto">
                      <DataTable
                        columns={columns}
                        data={formData.VaccineDetails}
                        pagination
                        highlightOnHover
                        striped
                        responsive
                        customStyles={tableCustomStyles}
                        subHeader
                        subHeaderComponent={
                          <TotalsSummary
                            totalQuantity={totalQuantity}
                            totalPrice={totalPrice}
                          />
                        }
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              <Box p={{ base: 2, md: 5 }}>
                {hasUnsavedChanges && (
                  <Alert status="warning" borderRadius="md" mb={4}>
                    <AlertIcon />
                    <Box>
                      <AlertTitle fontSize={{ base: "sm", md: "md" }}>
                        มีการแก้ไขที่ยังไม่ได้บันทึก
                      </AlertTitle>
                      <AlertDescription fontSize={{ base: "xs", md: "sm" }}>
                        กรุณากดปุ่มบันทึกก่อน จึงจะพิมพ์ใบจองหรือส่งออกเป็น PDF
                        ได้ เพื่อให้เอกสารที่พิมพ์ตรงกับข้อมูลในระบบ
                      </AlertDescription>
                    </Box>
                  </Alert>
                )}
                {/* MODIFICATION: Replaced HStack with a responsive Flex container for action buttons */}
                 <Flex
                  direction={{ base: "column", md: "row" }}
                  justifyContent={{ md: "flex-end" }}
                  alignItems={{ base: "stretch", md: "center" }}
                  gap={4}
                  wrap="wrap"
                  w="100%"
                >
                  <IconButton.Back onClick={handleBack} label="ย้อนกลับ" />
                  
                  {isEditable && (
                    <IconButton.Cancel
                      onClick={handleCancelReservation}
                      label="ยกเลิกการจอง"
                    />
                  )}
                  {isEditable && (
                    <IconButton.Save
                      onClick={handleConfirm}
                      isLoading={isLoading}
                    />
                  )}
                  <Tooltip
                    label="กรุณาบันทึกการแก้ไขก่อนพิมพ์เอกสาร"
                    isDisabled={!hasUnsavedChanges}
                    placement="top"
                    hasArrow
                  >
                    <Box w={{ base: "100%", md: "auto" }}>
                      <IconButton.Print
                        onClick={printTable}
                        label="พิมพ์ใบจอง"
                        isDisabled={hasUnsavedChanges}
                        w="100%"
                      />
                    </Box>
                  </Tooltip>
                  <Tooltip
                    label="กรุณาบันทึกการแก้ไขก่อนส่งออกเอกสาร"
                    isDisabled={!hasUnsavedChanges}
                    placement="top"
                    hasArrow
                  >
                    <Box w={{ base: "100%", md: "auto" }}>
                      <IconButton.ExportPdf
                        onClick={exportToPDF}
                        isDisabled={hasUnsavedChanges}
                        w="100%"
                      />
                    </Box>
                  </Tooltip>
                </Flex>
              </Box>

              <AlertDialog
                isOpen={isLeaveAlertOpen}
                leastDestructiveRef={leaveCancelRef}
                onClose={() => setIsLeaveAlertOpen(false)}
              >
                <AlertDialogOverlay>
                  <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                      มีการแก้ไขที่ยังไม่ได้บันทึก
                    </AlertDialogHeader>
                    <AlertDialogBody>
                      <Text>
                        ข้อมูลใบจองที่ท่านแก้ไขยังไม่ได้บันทึกลงระบบ
                        หากย้อนกลับตอนนี้ การแก้ไขทั้งหมดจะหายไป
                      </Text>
                      <Text mt={3}>คุณต้องการดำเนินการอย่างไร :</Text>
                    </AlertDialogBody>
                    <AlertDialogFooter
                      as={Flex}
                      direction={{ base: "column", sm: "row" }}
                      gap={3}
                      justifyContent="flex-end"
                    >
                      <Button
                        ref={leaveCancelRef}
                        colorScheme="backScheme"
                        leftIcon={<FontAwesomeIcon icon={faXmark} />}
                        onClick={() => setIsLeaveAlertOpen(false)}
                        w={{ base: "100%", sm: "auto" }}
                      >
                        กลับไปแก้ไขต่อ
                      </Button>
                      <Button
                        colorScheme="cancelScheme"
                        leftIcon={<FontAwesomeIcon icon={faTrash} />}
                        onClick={() => {
                          setIsLeaveAlertOpen(false);
                          goBackToList();
                        }}
                        w={{ base: "100%", sm: "auto" }}
                      >
                        ออกโดยไม่บันทึก
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialogOverlay>
              </AlertDialog>

              <AlertDialog
                isOpen={isAlertOpen}
                leastDestructiveRef={cancelRef}
                onClose={() => handleAlertConfirm("cancel")}
              >
                <AlertDialogOverlay>
                  <AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">
                      รายการวัคซีนซ้ำ
                    </AlertDialogHeader>
                    <AlertDialogBody>
                      <Text>
                        มีรายการวัคซีนชนิดนี้อยู่ในรายการวัคซีนที่ต้องการสั่งจองแล้ว
                      </Text>
                      {duplicateVaccine && (
                        <Text mt={2}>
                          <strong>{duplicateVaccine.name}</strong>
                          <br />
                          จำนวนเดิม :{" "}
                          {formatNumber(duplicateVaccine.RequestQuantity)} ขวด
                        </Text>
                      )}
                      <Text mt={3}>คุณต้องการดำเนินการอย่างไร :</Text>
                    </AlertDialogBody>
                    <AlertDialogFooter
                      as={Flex}
                      direction={{ base: "column", sm: "row" }}
                      gap={3}
                      justifyContent="flex-end"
                    >
                      <Button
                        ref={cancelRef}
                        colorScheme="backScheme"
                        leftIcon={<FontAwesomeIcon icon={faXmark} />}
                        onClick={() => handleAlertConfirm("cancel")}
                        w={{ base: "100%", sm: "auto" }}
                      >
                        ยกเลิก
                      </Button>
                      <Button
                        colorScheme="addScheme"
                        leftIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={() => handleAlertConfirm("add")}
                        w={{ base: "100%", sm: "auto" }}
                      >
                        เพิ่มจำนวน
                      </Button>
                      <Button
                        colorScheme="nextScheme"
                        leftIcon={<FontAwesomeIcon icon={faPenToSquare} />}
                        onClick={() => handleAlertConfirm("replace")}
                        w={{ base: "100%", sm: "auto" }}
                      >
                        ปรับจำนวน
                      </Button>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialogOverlay>
              </AlertDialog>
            </>
          )}
        </div>
      </Box>
    );
  };

  export default VaccineReservationForm;